<script setup>
import { Timestamp } from 'firebase/firestore';
import { isObject } from 'lodash';
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { useZonedTime } from '../composables/useZonedTime';
import HrTimeField from './HrTimeField.vue';

/**
 * Time range field — a pair of HrTimeField inputs for start and end times, with automatic end-time adjustment.
 *
 * When updating the start time would push the end time before it, the end time is advanced to the next day,
 * and the end field says so in its supporting text.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Collecting a start and end time stored as Firestore Timestamps (e.g. shift times, appointment windows)
 * @related HrTimeField
 * @event update:startTime Emitted when the start time changes; payload is a Firestore Timestamp or null.
 * @event update:endTime Emitted when the end time changes; payload is a Firestore Timestamp or null.
 * @event focus Forwarded from the underlying HrTimeField inputs.
 * @slot start-prepend Rendered before the start-time field. Receives the forwarded prepend slot props from HrTimeField.
 * @slot end-prepend Rendered before the end-time field. Receives the forwarded prepend slot props from HrTimeField.
 */
defineOptions({ name: 'HrTimeRangeField' });

/**
 * @typedef {'filled' | 'outlined'} HRTimeRangeFieldVariant
 */
const props = defineProps({
  /**
   * Visual container style derived from M3 text field variants.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /** Start time as a Firestore Timestamp. */
  startTime: {
    type: Object,
    default: null,
  },
  /** End time as a Firestore Timestamp. */
  endTime: {
    type: Object,
    default: null,
  },
  /** Floating label for the start time field. */
  fromLabel: {
    type: String,
    default: null,
  },
  /** Floating label for the end time field. */
  toLabel: {
    type: String,
    default: null,
  },
  /** Switches the start field to the error color role. */
  startError: {
    type: Boolean,
    default: false,
  },
  /** Switches the end field to the error color role. */
  endError: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims both fields. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Makes both fields non-editable while keeping them visually active. */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading indicator inside both fields. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Reduces both fields to the M3 dense density level. */
  dense: {
    type: Boolean,
    default: false,
  },
  /** Suppresses the reserved space below both fields for hint/error messages. */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /**
   * IANA timezone the times are read and written in (e.g. "America/New_York"). The application
   * supplies this from its own timezone policy (e.g. a user preference); the field itself has no
   * knowledge of where it comes from. When null, the host's system timezone is used.
   */
  timeZone: {
    type: String,
    default: null,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the start time changes; payload is a Firestore Timestamp or null. */
  'update:startTime': null,
  /**
   * Emitted when the end time changes; payload is a Firestore Timestamp or null.
   * Automatically advanced to the next day if it would fall before the start time.
   */
  'update:endTime': null,
  /** Forwarded from the underlying HrTimeField inputs. */
  focus: null,
});

const { t } = useI18n();
const {
  adjustDate, getHours, getMinutes, addToDate, getLocaleDateString,
} = useZonedTime(toRef(props, 'timeZone'));

// Compared as dates written out in the zone: two instants share a calendar day there exactly when
// their date strings match.
const endsNextDay = computed(() => props.startTime instanceof Timestamp
  && props.endTime instanceof Timestamp
  && getLocaleDateString(props.endTime) !== getLocaleDateString(props.startTime));

function adjustedEndTimestamp(startTimestamp, endTimestamp) {
  // Put the end's wall-clock time (as read in the active zone) on the start's calendar day,
  // then advance a day if that would fall before the start.
  const startDate = startTimestamp.toDate();
  const endDate = endTimestamp.toDate();

  let result = adjustDate(new Date(startDate), { hour: getHours(endDate), minute: getMinutes(endDate) });

  if (result < startDate) {
    result = addToDate(result, { days: 1 });
  }

  return Timestamp.fromDate(result);
}

function handleStartTimeUpdate(newStartTimestamp) {
  emit('update:startTime', newStartTimestamp);

  if (newStartTimestamp && isObject(props.endTime)) {
    emit('update:endTime', adjustedEndTimestamp(newStartTimestamp, props.endTime));
  }
}

function handleEndTimeUpdate(newEndTimestamp) {
  if (!newEndTimestamp || !isObject(props.startTime)) {
    emit('update:endTime', newEndTimestamp);
    return;
  }

  emit('update:endTime', adjustedEndTimestamp(props.startTime, newEndTimestamp));
}
</script>

<template>
  <div class="row q-col-gutter-sm">
    <hr-time-field
      :model-value="startTime"
      :variant="variant"
      :label="fromLabel"
      :error="startError"
      :disable="disable"
      :readonly="readonly"
      :loading="loading"
      :dense="dense"
      :hide-bottom-space="hideBottomSpace"
      :legacy-props="legacyProps"
      :time-zone="timeZone"
      preserve-date
      class="col"
      @update:model-value="handleStartTimeUpdate($event)"
      @focus="emit('focus', $event)"
    >
      <template
        v-if="$slots['start-prepend']"
        #prepend="slotProps"
      >
        <slot
          name="start-prepend"
          v-bind="slotProps"
        />
      </template>
    </hr-time-field>
    <hr-time-field
      :model-value="endTime"
      :variant="variant"
      :label="toLabel"
      :error="endError"
      :hint="endsNextDay ? t('common.textNextDay') : null"
      :disable="disable"
      :readonly="readonly"
      :loading="loading"
      :dense="dense"
      :hide-bottom-space="hideBottomSpace"
      :legacy-props="legacyProps"
      :time-zone="timeZone"
      class="col"
      @update:model-value="handleEndTimeUpdate($event)"
      @focus="emit('focus', $event)"
    >
      <template
        v-if="$slots['end-prepend']"
        #prepend="slotProps"
      >
        <slot
          name="end-prepend"
          v-bind="slotProps"
        />
      </template>
    </hr-time-field>
  </div>
</template>
